import React, { useState, useEffect } from "react";
import { auth, db } from "../firebase";
import {
  GoogleAuthProvider,
  FacebookAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  OAuthProvider,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";

const LoginSignupPage = () => {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Check Onboarding Status
  const checkOnboardingStatus = async (userId) => {
    try {
      const onboardingDocRef = doc(db, "onboardingData", userId);
      const onboardingDoc = await getDoc(onboardingDocRef);

      if (onboardingDoc.exists()) {
        navigate("/");
      } else {
        navigate("/profile-setup");
      }
    } catch (error) {
      console.error("Error checking onboarding status:", error);
      navigate("/profile-setup");
    }
  };

  // Google Login
  const handleGoogleLogin = async () => {
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      await checkOnboardingStatus(result.user.uid);
    } catch (error) {
      console.error(error.message);
    }
  };

  // Facebook Login
  const handleFacebookLogin = async () => {
    const provider = new FacebookAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      await checkOnboardingStatus(result.user.uid);
    } catch (error) {
      console.error(error.message);
    }
  };

  // Apple Login (Using OAuthProvider)
  const handleAppleLogin = async () => {
    const provider = new OAuthProvider("apple.com");
    try {
      const result = await signInWithPopup(auth, provider);
      await checkOnboardingStatus(result.user.uid);
    } catch (error) {
      console.error(error.message);
    }
  };

  // Email and Password Login
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      await checkOnboardingStatus(result.user.uid);
    } catch (error) {
      console.error(error.message);
      setError("Invalid email or password. Please try again.");
    }
  };

  // Track Auth State
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await checkOnboardingStatus(currentUser.uid);
      }
    });

    return () => unsubscribe();
  }, []);

  return (
    <div className="flex flex-col h-screen">
      {/* Header */}
      <Header />

      {/* Main Container */}
      <div className="flex flex-grow items-center justify-center bg-gradient-to-br from-teal-500 to-blue-700">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6 sm:p-8">
          <h2 className="text-3xl font-semibold text-center text-gray-800 mb-6">
            Login or Sign Up
          </h2>
          {error && <p className="text-red-500 text-center mb-4">{error}</p>}

          {/* Email/Password Login Form */}
          <form onSubmit={handleEmailLogin} className="mb-6">
            <div className="mb-4">
              <label className="block font-semibold mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>
            <div className="mb-4">
              <label className="block font-semibold mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>
            <button
              type="submit"
              className="w-full py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition"
            >
              Login
            </button>
          </form>

          <p className="text-center text-gray-600 mb-4">OR</p>

          {/* Google Login */}
          <button
            onClick={handleGoogleLogin}
            className="flex items-center justify-center w-full py-3 mb-4 bg-blue-500 text-white rounded-full hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-300 transition ease-in-out duration-200"
          >
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/2/2d/Logo_Google_blanco.png"
              alt="Google"
              className="w-6 h-6 mr-3"
            />
            Continue with Google
          </button>

          <p className="text-sm text-center text-gray-600 mt-4">
            Don't have an account?{" "}
            <button
              onClick={() => navigate("/create-account")}
              className="text-blue-500 hover:underline"
            >
              Sign Up
            </button>
          </p>
        </div>
      </div>

      {/* Footer */}

      <Footer />
    </div>
  );
};

export default LoginSignupPage;